/**
 * 動画に診療科（Specialty）が設定済みかどうかの判定。
 *
 * VideoCard / VideoCardSide / VideoCardMobile の3種で判定がバラバラだと、
 * 「一覧ページのマッパーがどのフィールドを残しているか」によって一部の画面だけ
 * 誤って disabled になる事故が起きる（2026-09-21 発生）。判定はこの関数だけに集約し、
 * 各カードコンポーネントは呼ぶだけにすること。
 */
export interface VideoWithSpecialtyStatus {
  /** VideoEdit系のリクエスト/レスポンスで使われるID直値。基本的にはこちらより specialty を優先する経路が多い */
  specialty_id?: number | string | null;
  /** バックエンドの Sequelize include 由来。未設定なら null（キー自体は常に存在する） */
  specialty?: { id?: number | string; name?: string } | null;
  /** 一覧系ページのマッパーが specialty オブジェクトから抜き出した名前のみのフォールバック */
  specialty_name?: string | null;
}

/**
 * 診療科が設定済みなら true。
 *
 * - `specialty_id` / `specialty`（オブジェクト） / `specialty_name` のいずれかに値があれば true。
 * - 3つとも `undefined`（＝呼び出し元がそもそも診療科の情報をこのオブジェクトに詰めていない）の
 *   ときは、判定不能として **true（活性）を返す**。詰め替えの過程で情報が単に渡っていないだけの
 *   可能性があり、「情報が無い＝disabled」にすると、詰め替えミスで公開系メニューが常時 disabled
 *   になる事故を再発する（2026-09-21、MyVideoList/MyFavoriteListのmapperがspecialty情報を
 *   落としていたために発生）。フェイルオープンとし、実際の必須化はサーバー側チェックに委ねる。
 * - 一方、バックエンドは動画取得APIで診療科未設定の動画にも `specialty: null` を明示的に返す
 *   （Sequelizeのinclude由来でキー自体は常に存在する）ため、`specialty === null` が
 *   マッパーを通じて伝われば「本当に未設定」と「渡されていないだけ」を区別できる。
 *   一覧ページのマッパー側は、この関数が判定を誤らないよう `specialty` を（null も含めて）
 *   明示的に引き継ぐこと（`specialty_name` だけの詰め替えはこの区別ができない）。
 */
export const hasSpecialtyAssigned = (video: VideoWithSpecialtyStatus): boolean => {
  const { specialty_id, specialty, specialty_name } = video;

  const noSpecialtyInfoProvided =
    specialty_id === undefined && specialty === undefined && specialty_name === undefined;
  if (noSpecialtyInfoProvided) {
    return true;
  }

  return Boolean(specialty_id || specialty?.id || specialty_name);
};
